import React, { useState, useRef, useEffect } from "react";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { ImageOff, Check, X, Maximize2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { addPendingChange, removePendingChange } from "../../store/flowSlice";
import { useToast } from "../../components/hooks/use-toast";
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "../../i18n/hooks";


interface ImageGalleryProps {
  images: string[];
  productId: string;
  jobId: string;
}

interface ImageState {
  loading: boolean;
  error: boolean;
}

interface ThumbnailListProps {
  images: string[];
  selectedImages: string[];
  focusedImage: string | null;
  onImageSelect: (image: string) => void;
  onImageFocus: (image: string) => void;
}

const ThumbnailList: React.FC<ThumbnailListProps> = React.memo(({
  images,
  selectedImages,
  focusedImage,
  onImageSelect,
  onImageFocus,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const { t } = useTranslation();

  const handleScroll = () => {
    if (listRef.current) {
      setScrollPosition(listRef.current.scrollTop);
    }
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = scrollPosition;
    }
  }, [focusedImage, scrollPosition]);

  return (
    <div
      ref={listRef}
      onScroll={handleScroll}
      className="h-[calc(100vh-160px)] overflow-y-auto"
    >
      <div className="grid grid-cols-3 md:grid-cols-1 gap-2">
        {images.map((image) => (
          <div
            key={image}
            onClick={() => onImageFocus(image)}
            className={`relative aspect-[4/3] m-2 p-2 rounded-lg overflow-hidden group
              ${focusedImage === image ? "ring-2 ring-blue-500" : ""}
              hover:ring-2 hover:ring-gray-300
            `}
          >
            <img
              src={image}
              alt={t('imageGallery.product_thumbnail')}
              className={`w-full h-full object-contain ${selectedImages.includes(image) ? "opacity-70" : ""
                }`}
            />
            <div className="absolute top-2 right-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onImageSelect(image);
                }}
                className={`p-2 rounded-full shadow-lg transform transition-all
                  ${selectedImages.includes(image)
                    ? "bg-blue-500 text-white scale-100 opacity-100"
                    : "bg-white text-gray-700 scale-75 opacity-0 group-hover:opacity-100 group-hover:scale-100"
                  }
                  hover:scale-110
                `}
                aria-label={t('imageGallery.select_image')}
              >
                <Check className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export const JOB_GALLERY_STORAGE_KEY = (jobId: string,productId: string) => `${jobId}_product_${productId}_selected_images`;

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, productId, jobId }) => {
  const [selectedImages, setSelectedImages] = useState<string[]>(() => {
    const saved = localStorage.getItem(JOB_GALLERY_STORAGE_KEY(jobId, productId));
    return saved ? JSON.parse(saved) : [];
  });
  const [focusedImage, setFocusedImage] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [imageStates, setImageStates] = useState<Record<string, ImageState>>({});

  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | null>(null);

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem(JOB_GALLERY_STORAGE_KEY(jobId, productId), JSON.stringify(selectedImages));

    // Show saving status
    setSaveStatus('saving');

    // Dispatch to Redux
    if(selectedImages.length > 0){
      dispatch(addPendingChange({
        enrichmentJobId: jobId,
        productId,
        fieldName: "radar_images",
        fieldValue: JSON.stringify(selectedImages)
      }));
    }else{
      dispatch(removePendingChange({
        enrichmentJobId: jobId,
        productId,
        fieldName: "radar_images"
      }));
    }


    // Set saved status after a brief delay
    const timer = setTimeout(() => {
      setSaveStatus('saved');
      // Clear the 'saved' message after 1.5s
      setTimeout(() => setSaveStatus(null), 1500);
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedImages, jobId, productId, dispatch]);

  const ModalView = () => focusedImage && createPortal(
    <div className="fixed inset-0 bg-black/95 z-[9999]" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* Full-width clickable areas for returning */}
      <div className="h-full w-full" onClick={() => setFocusedImage(null)}>


        {/* Center content - non-clickable */}
        <div className="w-[80%] max-w-6xl mx-auto" onClick={(e) => e.stopPropagation()}>          {/* Content container */}
          <div className="relative flex gap-4 p-4 h-screen items-center">

            {/* Close button */}
            <button
              onClick={() => setFocusedImage(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
              aria-label={t('imageGallery.close')}
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Main image */}
            <div className="flex-1 flex items-center justify-center">
              <img
                src={focusedImage}
                alt={t('imageGallery.featured_product_image')}
                className="max-w-full max-h-[80vh] object-contain"
              />
            </div>

            {/* Thumbnails */}
            <div className="w-48 flex-shrink-0">
              <div className="bg-black/50 px-3 py-2 rounded-lg mb-4 text-white text-sm flex items-center justify-between">
                <div>{t('imageGallery.image_count_of_total', { count: selectedImages.length, total: images.length })}</div>
                {selectedImages.length > 0 && (
                  <button
                    onClick={() => setSelectedImages([])}
                    className="text-gray-300 hover:text-white"
                  >
                    {t('imageGallery.clear')}
                  </button>
                )}
              </div>
              <ThumbnailList
                images={images}
                selectedImages={selectedImages}
                focusedImage={focusedImage}
                onImageSelect={(image) => {
                  setSelectedImages((prev) =>
                    prev.includes(image)
                      ? prev.filter((i) => i !== image)
                      : [...prev, image]
                  );
                }}
                onImageFocus={setFocusedImage}
              />
            </div>
          </div>
        </div>

        {/* Right clickable area */}
        <div
          className="flex-1 cursor-pointer"
          onClick={() => setFocusedImage(null)}
        />
      </div>
    </div>,
    document.body
  );

  return (
    <div className="space-y-4">
      <Alert className="bg-blue-50 border-blue-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <AlertDescription className="text-blue-800">
            {t('imageGallery.new_images_found', { count: images.length })}
          </AlertDescription>
        </div>
      </Alert>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">{t('imageGallery.product_images')}</h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {t('imageGallery.selected_of_total', { selected: selectedImages.length, total: images.length })}
            </span>
            {saveStatus && (
              <span className={`text-sm ${saveStatus === 'saving' ? 'text-gray-400' : 'text-green-500'
                }`}>
                • {saveStatus === 'saving' ? t('imageGallery.saving') : t('imageGallery.saved')}
              </span>
            )}
          </div>
          {selectedImages.length > 0 && (
            <button
              onClick={() => setSelectedImages([])}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {t('imageGallery.clear')}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {(showAll ? images : images.slice(0, 10)).map((image, index) => (
            <div
              key={image}
              className={`group relative w-full bg-gray-50 rounded-lg overflow-hidden
                ${selectedImages.includes(image) ? "ring-2 ring-blue-500" : ""}
                hover:ring-2 hover:ring-gray-300
              `}
              onClick={() => {
                setSelectedImages(prev =>
                  prev.includes(image)
                    ? prev.filter(i => i !== image)
                    : [...prev, image]
                );
              }}
            >
              {/* Maintain aspect ratio container */}
              <div className="relative w-full pb-[100%]">
                <div className="absolute inset-0 p-2">
                  <img
                    src={image}
                    alt={`${t('imageGallery.product_thumbnail')} ${index + 1}`}
                    className={`w-full h-full object-contain transition-opacity duration-200
                      ${selectedImages.includes(image) ? "opacity-80" : ""}
                    `}
                    onLoad={() => {
                      setImageStates(prev => ({
                        ...prev,
                        [image]: { loading: false, error: false }
                      }));
                    }}
                    onError={() => {
                      setImageStates(prev => ({
                        ...prev,
                        [image]: { loading: false, error: true }
                      }));
                    }}
                  />
                </div>
              </div>

              {/* Expand button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFocusedImage(image);
                }}
                className="absolute top-2 left-2 p-1.5 rounded-lg bg-black/50 text-white
                  opacity-0 group-hover:opacity-100 transition-opacity duration-200
                  hover:bg-black/70"
                title={t('imageGallery.view_full_size')}
              >
                <Maximize2 className="w-4 h-4" />
              </button>

              {/* Selection indicator */}
              <div className={`absolute top-2 right-2 p-1.5 rounded-lg 
                ${selectedImages.includes(image)
                  ? 'bg-blue-500 text-white'
                  : 'bg-black/50 text-white opacity-0 group-hover:opacity-100'}
                transition-all duration-200`}
              >
                <Check className="w-4 h-4" />
              </div>

              {/* Error state */}
              {imageStates[image]?.error && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <ImageOff className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>
          ))}
        </div>

        {!showAll && images.length > 10 && (
          <button
            onClick={() => setShowAll(true)}
            className="w-full py-2 px-4 text-sm text-gray-600 hover:text-gray-900
              bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            {t('imageGallery.show_more_images', { count: images.length - 10 })}
          </button>
        )}
      </div>

      {focusedImage && <ModalView />}
    </div>
  );
};

export default ImageGallery;